import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { alert, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { formatAmount } from '../lib/format';
import type { Currency, Summary } from '../lib/types';

interface Props {
  onDone: () => void;
  onBack: () => void;
}

export default function Reconcile({ onDone, onBack }: Props) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [usd, setUsd] = useState('');
  const [syp, setSyp] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.summary()
      .then((s) => {
        setSummary(s);
        // Prefill with the current balances so the user only edits what's off.
        setUsd(s.balances.USD ? s.balances.USD.toFixed(2) : '0');
        setSyp(s.balances.SYP ? String(Math.round(s.balances.SYP)) : '0');
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'failed to load'))
      .finally(() => setLoading(false));
  }, []);

  function diffFor(cur: Currency): number | null {
    if (!summary) return null;
    const raw = cur === 'USD' ? usd : syp;
    const actual = raw.trim() === '' ? null : parseAmountInputAllowZero(raw);
    if (actual === null) return null;
    return actual - summary.balances[cur];
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const u = usd.trim() === '' ? null : parseAmountInputAllowZero(usd);
    const s = syp.trim() === '' ? null : parseAmountInputAllowZero(syp);
    if (u === null || s === null) { setErr('Enter valid amounts (0 or more).'); return; }
    setErr(null);
    setBusy(true);
    try {
      const res = await api.reconcile(u, s);
      haptic('success');
      alert(res.message ?? `Balance reconciled — ${res.entries.length} adjustment(s) made.`);
      onDone();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to reconcile');
    } finally {
      setBusy(false);
    }
  }

  const usdDiff = diffFor('USD');
  const sypDiff = diffFor('SYP');

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Reconcile balance</h1>
      </div>
      {err && <div className="error">{err}</div>}

      {loading ? (
        <div className="card"><div className="muted">Loading…</div></div>
      ) : (
        <form onSubmit={submit}>
          <div className="card">
            <p className="muted small" style={{ marginTop: 0 }}>
              Count your actual cash and enter it below. The app will add a correcting
              entry so your balance matches reality.
            </p>

            <label className="label">Actual USD you have</label>
            <AmountInput value={usd} onChange={setUsd} currency="USD" placeholder="0" autoFocus />
            <DiffLine diff={usdDiff} currency="USD" appBalance={summary?.balances.USD ?? 0} />

            <label className="label">Actual SYP you have</label>
            <AmountInput value={syp} onChange={setSyp} currency="SYP" placeholder="0" />
            <DiffLine diff={sypDiff} currency="SYP" appBalance={summary?.balances.SYP ?? 0} />

            <p className="muted small" style={{ marginTop: 14, marginBottom: 0 }}>
              Tip: a reconcile is a quick fix. If the gap is from forgotten purchases,
              adding them as real expenses keeps your Reports accurate.
            </p>
          </div>

          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Reconciling…' : 'Reconcile balance'}
          </button>
        </form>
      )}
    </>
  );
}

function DiffLine({ diff, currency, appBalance }: { diff: number | null; currency: Currency; appBalance: number }) {
  if (diff === null) {
    return <div className="muted small" style={{ marginTop: 4 }}>App currently shows {formatAmount(appBalance, currency)}</div>;
  }
  if (Math.abs(diff) < 0.005) {
    return <div className="muted small" style={{ marginTop: 4 }}>✓ Matches the app ({formatAmount(appBalance, currency)})</div>;
  }
  const cls = diff < 0 ? 'amount-neg' : 'amount-pos';
  const sign = diff < 0 ? '−' : '+';
  return (
    <div className="small" style={{ marginTop: 4 }}>
      <span className="muted">App shows {formatAmount(appBalance, currency)} → </span>
      <span className={cls}>will add {sign}{formatAmount(Math.abs(diff), currency)} adjustment</span>
    </div>
  );
}

// Like parseAmountInput but allows 0 (you can genuinely have zero of a currency).
function parseAmountInputAllowZero(s: string): number | null {
  const cleaned = s.replace(/[,\s]/g, '');
  if (cleaned === '') return null;
  const n = parseFloat(cleaned);
  if (!isFinite(n) || n < 0) return null;
  return n;
}
